import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const load = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => load("uplink_cart", []));
  const [notes, setNotes] = useState(() => load("uplink_notes", ""));
  const [method, setMethod] = useState(() => load("uplink_method", "qris"));
  const [useCoins, setUseCoins] = useState(false);
  const [outlet, setOutlet] = useState(() => load("uplink_outlet", ""));
  const [shop, setShop] = useState({ outlets: [], taxRate: 10, taxName: "PB1" });
  const [seat, setSeat] = useState(() => load("uplink_seat", null));
  const [myOrders, setMyOrders] = useState(() => load("uplink_orders", []));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => { localStorage.setItem("uplink_cart", JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem("uplink_notes", JSON.stringify(notes)); }, [notes]);
  useEffect(() => { localStorage.setItem("uplink_method", JSON.stringify(method)); }, [method]);
  useEffect(() => { localStorage.setItem("uplink_seat", JSON.stringify(seat)); }, [seat]);
  useEffect(() => { localStorage.setItem("uplink_outlet", JSON.stringify(outlet)); }, [outlet]);
  useEffect(() => { localStorage.setItem("uplink_orders", JSON.stringify(myOrders)); }, [myOrders]);

  const add = (m) =>
    setItems((prev) => {
      const found = prev.find((i) => i.id === m.id);
      if (found) return prev.map((i) => (i.id === m.id ? { ...i, qty: i.qty + 1 } : i));
      return [...prev, { id: m.id, name: m.name, price: m.price, image: m.image, qty: 1 }];
    });

  const setQty = (id, qty) =>
    setItems((prev) => (qty <= 0 ? prev.filter((i) => i.id !== id) : prev.map((i) => (i.id === id ? { ...i, qty } : i))));

  const setItemNote = (id, note) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, note: note.slice(0, 100) } : i)));

  const clear = () => { setItems([]); setNotes(""); setUseCoins(false); };

  const syncWithMenu = (menu) =>
    setItems((prev) => prev.flatMap((i) => {
      const m = menu.find((x) => x.id === i.id);
      return m ? [{ ...i, name: m.name, price: m.price, image: m.image }] : [];
    }));

  const rememberOrder = (o) => setMyOrders((prev) => [{ id: o.id, token: o.access_token, code: o.code }, ...prev].slice(0, 30));

  const value = useMemo(() => {
    const count = items.reduce((s, i) => s + i.qty, 0);
    const total = items.reduce((s, i) => s + i.qty * i.price, 0);
    return {
      items, notes, setNotes, method, setMethod, useCoins, setUseCoins, seat, setSeat, myOrders, rememberOrder, outlet, setOutlet, shop, setShop,
      pickerOpen, setPickerOpen, sheetOpen, setSheetOpen, add, setQty, setItemNote, clear, syncWithMenu, count, total,
    };
  }, [items, notes, method, useCoins, seat, myOrders, pickerOpen, sheetOpen, outlet, shop]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
